import { useRef } from "react";
import { useLocation } from "react-router-dom";
import { z } from "zod";
import { gaPushEvent } from "../../../tools/analytics/base.analytics";
import { gaEventPageView } from "../../../tools/analytics/common.analytics";
import { useCurrentUser } from "../../user/hooks/useCurrentUser";
import { extractRoomIdFromPath } from "../../room/utils/roomIdFromPath";
import { useApiQuery } from "../../../services/api";
import { roomDiscoveryChannelSchema } from "../../../tools/analytics/domains/invite.analytics";
import { DebugLogger } from "../utils/debugLogger";
import { useLocationState } from "./useLocationState";
import { useEffectOnce } from "./useEffectOnce";

const debug = new DebugLogger("Router Page View");
debug.mute();

// NOTE: These pages will not trigger a page view automatically.
const MANUAL_PAGE_VIEW = [
  "/post",
  "/room/post",

  "/video",
  "/room/video",
  "/video/story",
  "/room/video/story",
  "/infographic",
  "/room/infographic",
  "/playlist/public",
  "/room/playlist",
];

// How the user got to this room, when the navigation bothered to say. Only
// `room_opened` reads it, and only to attribute an ACTIVATION: a member cannot
// fire `room_joined`, so without a channel on `room_opened` the half of the
// JuisciGPT feature that brings existing members back is invisible in the data
// (PRD AC-B7). Defaults to "public app" exactly like the join path, so a cold
// entry — a bookmark, a hard reload, a deep link — is never mis-attributed.
const roomEntryStateSchema = z.object({
  roomDiscoveryChannel: roomDiscoveryChannelSchema.default("public app"),
});

export function useRouterPageView() {
  const location = useLocation();
  const { currentUser } = useCurrentUser();
  const { roomDiscoveryChannel } = useLocationState(roomEntryStateSchema);
  const roomId = extractRoomIdFromPath(location.pathname);
  const isInRoom = roomId !== null;
  // Router-level exception: useRouterPageView runs above any specific Route,
  // so it must resolve the room itself. useApiQuery is React-tracked — the
  // hook re-runs once the query resolves, and only then does it fire
  // pageview / room_opened with the real room name.
  const roomQuery = useApiQuery(
    "/webapp/private/rooms/:id",
    roomId !== null ? { param: { id: roomId } } : false
  );
  const currentRoom = roomQuery.state === "success" ? roomQuery.body : null;
  const isRoomLoaded = roomId === null || roomQuery.state === "success";

  const memPathname = useRef("");
  const wasInRoomRef = useRef(false);
  const prevRoomIdRef = useRef<string | null>(null);
  const isManual = MANUAL_PAGE_VIEW.some((prefix) => {
    return location.pathname.startsWith(prefix);
  });

  useEffectOnce(() => {
    if (location.pathname === "/") return;
    if (location.pathname === "/profile" && !currentUser) return;
    if (isManual) return;

    // On room routes, wait for the room query to resolve before firing any
    // event — otherwise pageview / room_opened ship with null room info and
    // Mixpanel records "public app" for every initial room page load.
    if (!isRoomLoaded) return;

    if (location.pathname === memPathname.current) return;

    debug.log("Trigger page view", { isManual, isInRoom });

    // Track room entry/exit/switch transitions
    if (
      isInRoom &&
      currentRoom &&
      (!wasInRoomRef.current || roomId !== prevRoomIdRef.current)
    ) {
      const isMember = currentUser?.rooms.includes(roomId) ?? false;
      gaPushEvent("room_opened", {
        room_id: roomId,
        room_name: currentRoom.translations.en.name,
        member_of_room: isMember,
        room_discovery_channel: roomDiscoveryChannel,
      });
    } else if (!isInRoom && wasInRoomRef.current) {
      gaPushEvent("room_left", { room_id: prevRoomIdRef.current });
    }
    wasInRoomRef.current = isInRoom;
    prevRoomIdRef.current = roomId;

    void gaEventPageView(currentRoom);
    memPathname.current = location.pathname;
  }, [isRoomLoaded, location.pathname]);

  return {};
}
